import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ipdApi } from '@/api/ipd';
import type { Doctor, IPDQueueEntry } from '@/types/ipd';
import { ensureDoctorPrefix } from '@/lib/doctorName';
import { DoctorSearchableSelect } from '@/components/common/DoctorSearchableSelect';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: IPDQueueEntry | null;
  onDoctorChanged: (updatedEntry: IPDQueueEntry) => void;
};

export default function ChangeIPDDoctorDialog({
  open,
  onOpenChange,
  entry,
  onDoctorChanged,
}: Props) {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [doctorId, setDoctorId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const current = entry?.admission?.assignedDoctor?.id ?? '';
    setDoctorId(current);
    setLoadingDoctors(true);
    ipdApi
      .getDoctors()
      .then((res) => setDoctors(res.data?.data ?? []))
      .catch(() => toast.error('Failed to load doctors'))
      .finally(() => setLoadingDoctors(false));
  }, [open, entry?.id, entry?.admission?.assignedDoctor?.id]);

  const handleSave = async () => {
    if (!entry?.admission?.id) return;
    if (!doctorId) {
      toast.error('Select a doctor');
      return;
    }
    setSaving(true);
    try {
      const res = await ipdApi.changeAdmissionDoctor(entry.admission.id, doctorId);
      const payload = res.data?.data;
      const doctor = payload?.assignedDoctor;
      if (!doctor || !entry.admission) {
        toast.error('Could not update doctor');
        return;
      }
      const updatedEntry: IPDQueueEntry = {
        ...entry,
        admission: {
          ...entry.admission,
          assignedDoctor: {
            id: doctor.id,
            name: doctor.name,
            specialisation: doctor.specialisation,
          },
        },
      };
      toast.success(
        payload.previousDoctor?.id === doctor.id
          ? 'Doctor unchanged'
          : `Consulting doctor updated to ${ensureDoctorPrefix(doctor.name)}`
      );
      onOpenChange(false);
      onDoctorChanged(updatedEntry);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Failed to change doctor');
    } finally {
      setSaving(false);
    }
  };

  const currentName = entry?.admission?.assignedDoctor
    ? ensureDoctorPrefix(entry.admission.assignedDoctor.name)
    : 'Not assigned';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change consulting doctor</DialogTitle>
        </DialogHeader>
        {entry && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {entry.patient?.name} · {entry.ipdNumber ?? '—'}
            </p>
            <p className="text-sm">
              Current: <span className="font-medium">{currentName}</span>
            </p>
            <div className="space-y-2">
              <Label>New consulting doctor</Label>
              <DoctorSearchableSelect
                doctors={doctors}
                value={doctorId}
                onValueChange={setDoctorId}
                disabled={loadingDoctors || saving}
                placeholder={loadingDoctors ? 'Loading…' : 'Select doctor'}
              />
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving || loadingDoctors}>
            {saving ? 'Saving…' : 'Save & print stickers'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
